<template>
  <div class="tinymce-editor-wrapper">
    <Editor
      v-model="content"
      tinymce-script-src="/tinymce/tinymce.min.js"
      license-key="gpl"
      :init="editorConfig"
    />
  </div>
</template>

<script setup>
import { ref, watch, nextTick, defineProps, defineEmits, defineExpose } from 'vue'
import Editor from '@tinymce/tinymce-vue'
import emailTemplateService from '@/services/emailTemplateService'
import { useToast } from 'vue-toastification'

const toast = useToast()

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  height: {
    type: [String, Number],
    default: 500
  },
  placeholder: {
    type: String,
    default: '请输入内容...'
  }
})

const emit = defineEmits(['update:modelValue', 'focus'])

const content = ref(props.modelValue)
const editorInstance = ref(null)
const isUpdatingFromProp = ref(false) // 🔧 标志位：防止循环更新

const syncPlaceholder = (editor) => {
  const body = editor.getBody()
  if (!body) return
  const text = (body.innerText || body.textContent || '').trim()
  if (text) {
    body.removeAttribute('data-mce-placeholder')
    body.setAttribute('aria-placeholder', '')
  } else {
    body.setAttribute('data-mce-placeholder', props.placeholder)
  }
}

// TinyMCE 配置（使用自托管版本，完全免费）
const editorConfig = {
  // 🔧 TinyMCE 8.x 自托管开源版本
  base_url: '/tinymce',
  suffix: '.min',
  license_key: 'gpl',  // 🔧 TinyMCE 8.x 仍需要声明 GPL 许可
  
  // 高度
  height: typeof props.height === 'number' ? props.height : parseInt(props.height) || 500,
  
  // 菜单栏（移除 table）
  menubar: 'file edit view insert format tools',
  
  // 🔧 工具栏（撤销/重做移到第二行最后）
  toolbar: [
    'undo redo | blocks forecolor backcolor bold italic underline strikethrough link image removeformat code | fontsize fontfamily | alignleft aligncenter alignright alignjustify',
    'bullist numlist outdent indent | media table'
  ].join(' | '),
  
  // 🔧 工具栏模式：sliding = 可折叠展开（默认显示部分，点击按钮展开更多）
  toolbar_mode: 'floating',
  
  // 插件
  plugins: [
    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
    'insertdatetime', 'media', 'table', 'help', 'wordcount'
  ],
  
  // 禁用推广和高级功能
  promotion: false,
  branding: false,
  statusbar: false,  // 🔧 隐藏状态栏（包括"按 Alt+0 获得帮助"提示）
  
  // ========================================
  // 🔧 精简方案：只保留核心配置（10个核心配置项）
  // ========================================
  
  // 1. 完全允许所有 HTML 元素和属性
  valid_elements: '*[*]',
  extended_valid_elements: '*[*]',
  
  // 2. 使用原始编码，不转换实体
  entity_encoding: 'raw',
  
  // 3. 禁用 URL 转换
  convert_urls: false,
  
  // 4. 禁用自动清理
  verify_html: false,
  
  // 5. 禁用自动格式化
  indent: false,
  
  // 6. 内容样式
  content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }',
  
  // 🔧 启用 Undo/Redo（测试是否会导致格式化）
  // custom_undo_redo_levels: 0,  // 已注释，允许撤销/重做
  
  // 🔧 中文语言配置
  language: 'zh_CN',
  language_url: '/tinymce/langs/zh_CN.js',
  
  // 🔧 字体配置
  font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
  font_family_formats: '微软雅黑=Microsoft YaHei,Helvetica Neue,PingFang SC,sans-serif;苹果苹方=PingFang SC,Microsoft YaHei,sans-serif;宋体=simsun,serif;仿宋体=FangSong,serif;黑体=SimHei,sans-serif;Arial=arial,helvetica,sans-serif;Arial Black=arial black,avant garde;Book Antiqua=book antiqua,palatino;Comic Sans MS=comic sans ms,sans-serif;Courier New=courier new,courier;Georgia=georgia,palatino;Helvetica=helvetica;Impact=impact,chicago;Times New Roman=times new roman,times;Trebuchet MS=trebuchet ms,geneva;Verdana=verdana,geneva',
  
  // 图片上传配置（使用 /email-templates/api/v2/templates/upload_image/ API）
  images_upload_handler: async (blobInfo, progress) => {
    try {
      const formData = new FormData()
      // 🔧 后端 API 使用 'upload' 字段名（与 RichTextEditor 一致）
      formData.append('upload', blobInfo.blob(), blobInfo.filename())
      
      const response = await emailTemplateService.uploadImage(formData)
      
      if (response && response.success && response.url) {
        return response.url
      } else {
        throw new Error(response.message || '上传失败')
      }
    } catch (error) {
      console.error('图片上传失败:', error)
      toast.error(error.message || '图片上传失败')
      throw error
    }
  },
  
  // 自动上传图片
  automatic_uploads: true,
  
  // 图片选项
  image_advtab: true,
  image_caption: true,
  
  // 链接选项
  link_context_toolbar: true,
  link_title: false,
  default_link_target: '_blank',
  
  // 表格选项
  table_toolbar: 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
  table_appearance_options: true,
  table_advtab: true,
  table_cell_advtab: true,
  table_row_advtab: true,
  
  // 8. 粘贴选项（保留粘贴的图片和格式）
  paste_data_images: true,
  paste_merge_formats: false,
  
  // 代码编辑器
  code_dialog_width: 800,
  code_dialog_height: 600,
  
  // 允许内联样式
  allow_conditional_comments: true,
  allow_html_in_named_anchor: true,
  
  // 占位符
  placeholder: props.placeholder,
  
  // 浏览器拼写检查
  browser_spellcheck: true,
  
  // 🔧 初始化回调：直接操作 DOM，完全绕过 TinyMCE 的 HTML 处理
  init_instance_callback: (editor) => {
    editorInstance.value = editor
    
    // 直接设置 body 的 innerHTML，完全绕过 TinyMCE 的清理
    if (props.modelValue) {
      const body = editor.getBody()
      body.innerHTML = props.modelValue
      syncPlaceholder(editor)
    }
  },
  
  // ========================================
  // 🔧 精简方案：只保留2个核心事件（已恢复 Undo/Redo 用于测试）
  // ========================================
  setup: (editor) => {
    // 1. 强制使用 raw 格式（设置/获取内容时不格式化）
    editor.on('BeforeSetContent', (e) => {
      if (isUpdatingFromProp.value) {
        e.preventDefault()
        return false
      }
      e.format = 'raw'
    })
    
    editor.on('BeforeGetContent', (e) => {
      e.format = 'raw'
    })
    
    // 2. 直接获取 DOM 的 innerHTML（不经过 TinyMCE API）
    editor.on('change keyup', () => {
      if (isUpdatingFromProp.value) return
      content.value = editor.getBody().innerHTML
      syncPlaceholder(editor)
    })
    
    // 3. 🔧 监听编辑器获得焦点事件
    editor.on('focus', () => {
      emit('focus')
    })
    
    // 🔧 注意：Undo/Redo 已启用，测试是否会导致 HTML 格式化
  }
}

// 监听 modelValue 变化（切换模板时触发）
watch(() => props.modelValue, async (newValue) => {
  if (content.value !== newValue && editorInstance.value) {
    try {
      // 🔧 提前设置标志位，确保在 DOM 操作前就设置好
      isUpdatingFromProp.value = true
      
      // 等待一个微任务，确保标志位生效
      await nextTick()
      
      // 🔧 直接操作 DOM，完全绕过 TinyMCE 的 HTML 清理
      const body = editorInstance.value.getBody()
      if (body) {
        body.innerHTML = newValue || ''
        content.value = newValue
        syncPlaceholder(editorInstance.value)
      }
      
      // 再等待 DOM 更新完成
      await nextTick()
      
      // 等待 TinyMCE 的异步事件处理完成
      await new Promise(resolve => setTimeout(resolve, 200))
    } finally {
      // 重置标志位
      isUpdatingFromProp.value = false
    }
  }
})

// 监听内容变化
watch(content, (newValue) => {
  emit('update:modelValue', newValue)
})

// 编辑器初始化
// 插入变量（供父组件调用）
const insertVariable = (variable) => {
  if (editorInstance.value) {
    // 🔧 使用 text 格式插入，防止 {{ }} 被转义为 HTML 实体
    editorInstance.value.insertContent(variable, { format: 'text' })
    editorInstance.value.focus()
  }
}

// 聚焦编辑器
const focus = () => {
  if (editorInstance.value) {
    editorInstance.value.focus()
  }
}

// 获取 HTML 内容
const getHTML = () => {
  return editorInstance.value ? editorInstance.value.getContent() : content.value
}

// 设置 HTML 内容
const setHTML = (html) => {
  if (editorInstance.value) {
    editorInstance.value.setContent(html || '')
  }
  content.value = html
}

// 暴露方法给父组件
defineExpose({
  insertVariable,
  focus,
  getHTML,
  setHTML,
  editor: editorInstance
})
</script>

<style scoped>
.tinymce-editor-wrapper {
  width: 100%;
}

/* TinyMCE 容器样式调整 */
.tinymce-editor-wrapper :deep(.tox-tinymce) {
  border: 1px solid #e5e7eb;
  border-radius: 0;
}

.tinymce-editor-wrapper :deep(.tox-editor-header) {
  border-bottom: 1px solid #e5e7eb;
}

.tinymce-editor-wrapper :deep(.tox-toolbar) {
  background: #f9fafb;
}

/* 有内容时隐藏 placeholder，避免与正文重叠 */
.tinymce-editor-wrapper :deep(.mce-content-body:not([data-mce-placeholder])::before),
.tinymce-editor-wrapper :deep(.mce-content-body[data-mce-placeholder=""]::before) {
  display: none !important;
  content: none !important;
}
</style>

